<template>
  <div v-if="initialized">
    <table-wrapper
      v-if="tableData"
      id="data-table"
      ref="table"
      :fields="tableData.fields"
      :items="getItems"
      :per-page="pagination.perPage"
      :current-page="pagination.currentPage"
      :filter="tableData.filter"
      :responsive="true"
      :busy="isLoading"
      :sticky-header="true"
      :show-empty="true"
      :empty-text="noTableDataTitle"
      :empty-filtered-text="noTableDataTitle"
      :label-sort-asc="sortAscTitle"
      :label-sort-desc="sortDescTitle"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      fixed
      @refreshed="resetSelectedRow"
    >
      <template #head(check)>
        <b-form-checkbox
          v-if="selectableRows"
          id="checkbox__all"
          v-model="allSelected"
          :checked="allSelected"
          name="checkbox__all"
          @change="toggleSelectAll"
        />
        <a v-if="hasDetailsRow" class="row-details-btn" :class="selectableRows ? 'row-details-btn-spaced' : ''" @click="toggleAllRowsDetails">
          <img :src="allDetailsShowing ? collapseOpenIcon : collapseClosedIcon" />
        </a>
      </template>
      <template #cell(check)="row">
        <b-form-checkbox
          v-if="selectableRows"
          :id="'checkbox__' + row.item.id"
          v-model="row.item.isSelected"
          :checked="row.item.isSelected"
          :disabled="row.item.selectDisabled"
          :name="'checkbox__' + row.item.id"
          @change="toggleSelectRow(row.item)"
        />
        <a v-if="hasDetailsRow" class="row-details-btn" :class="selectableRows ? 'row-details-btn-spaced' : ''" @click="row.toggleDetails">
          <img :src="row.detailsShowing ? collapseOpenIcon : collapseClosedIcon" />
        </a>
        <a v-if="editableRows" v-b-tooltip.hover href="#" class="edit-row-button" :title="'edit' | i18n" @click="onEdit(row.item)">
          <BIconPencilSquare />
        </a>
      </template>
      <template #table-busy>
        <div class="text-center">
          <b-spinner class="align-middle" />
          <strong class="ml-1">{{ 'loading' | i18n }}...</strong>
        </div>
      </template>
    </table-wrapper>
    <!-- <b-pagination
      v-model="pagination.currentPage"
      class="table-pagination"
      :total-rows="pagination.totalCount"
      :per-page="pagination.perPage"
      limit="13"
      aria-controls="data-table"
      size="sm"
    /> -->
  </div>
</template>

<script>
import { NO_TABLE_DATA_TITLE, SORT_ASC_TITLE, SORT_DESC_TITLE } from '../../constants';
import TableWrapper from './table-wrapper';
import collapseOpenIcon from '../../assets/icons/collapse-open.svg';
import collapseClosedIcon from '../../assets/icons/collapse-closed.svg';
import { BIconPencilSquare } from 'bootstrap-vue';

export default {
  name: 'DataTable',
  components: { TableWrapper, BIconPencilSquare },
  props: {
    getPage: {
      type: Function,
      default: () => []
    },
    selectableRows: {
      type: Boolean,
      default: true
    },
    value: {
      type: Object,
      default: () => ({
        editedRow: null,
        selectedRow: null,
        rows: [],
        filter: {},
        sortBy: 'id',
        sortDesc: true
      })
    },
    perPage: {
      type: Number,
      default: 10
    },
    hasDetailsRow: {
      type: Boolean,
      default: false
    },
    editableRows: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      pagination: {
        currentPage: 1,
        perPage: 10,
        totalCount: 0
      },
      isLoading: false,
      allSelected: false,
      allDetailsShowing: false,
      tableData: {
        editedRow: null,
        selectedRow: null,
        selectedRows: [],
        rows: [],
        filter: this.$parent.table && this.$parent.table.filter ? { ...this.$parent.table.filter } : {},
        sortBy: 'id',
        sortDesc: true
      },
      noTableDataTitle: 'Keine Daten',
      sortAscTitle: 'Klicken Sie hier, um aufsteigend zu sortieren',
      sortDescTitle: 'Klicken Sie hier, um in absteigender Reihenfolge zu sortieren',
      initialized: false,
      collapseOpenIcon,
      collapseClosedIcon
    };
  },
  computed: {
    selectedRows: function() {
      return this.tableData.rows.filter(r => r.isSelected);
    }
  },
  watch: {
    tableData: {
      handler(newVal) {
        this.$emit('input', newVal);
      },
      deep: true
    },
    value(newVal) {
      this.tableData = newVal;
    },
    selectedRows(newVal) {
      this.tableData.selectedRows = newVal;
    }
  },
  mounted() {
    if (this.value) {
      Object.keys(this.value).forEach(key => {
        this.tableData[key] = this.value[key];
      });
    }
    if (this.perPage) {
      this.pagination.perPage = this.perPage;
    }

    this.initialized = true;
  },
  methods: {
    async getItems(ctx) {
      try {
        const requestFilters = {};
        if (ctx.filter) {
          Object.keys(ctx.filter)
            .filter(key => !!ctx.filter[key])
            .forEach(key => {
              requestFilters[key] = ctx.filter[key];
            });
        }

        const requestParams = {
          page: ctx.currentPage,
          limit: ctx.perPage,
          sortBy: ctx.sortBy,
          sortOrder: ctx.sortDesc ? 'desc' : 'asc',
          ...requestFilters
        };

        this.isLoading = true;
        const { totalCount, data } = await this.getPage(requestParams);
        this.isLoading = false;
        this.pagination.totalCount = totalCount;
        this.tableData.rows = data.map(item => ({ ...item, editMode: false, isSelected: false, _showDetails: this.allDetailsShowing }));
        return this.tableData.rows;
      } catch (err) {
        this.isLoading = false;
        // this.showNotification({type: 'error', message: err.message});
        return [];
      }
    },
    toggleSelectRow(row) {
      const isSelected = !row.isSelected;
      const prevSelectedRows = this.selectedRows;
      const newSelectedRows = isSelected ? prevSelectedRows.concat([row]) : prevSelectedRows.filter(p => p.id !== row.id);

      if (newSelectedRows.length === 1) {
        this.tableData.selectedRow = isSelected ? row : newSelectedRows[0];
        this.allSelected = false;
      } else {
        this.tableData.selectedRow = null;
        this.tableData.editedRow = null;
      }
    },
    toggleSelectAll() {
      this.allSelected = !this.allSelected;
      this.tableData.rows.forEach(c => (c.isSelected = this.allSelected));
      this.tableData.selectedRow = null;
    },
    toggleAllRowsDetails() {
      this.allDetailsShowing = !this.allDetailsShowing;
      this.tableData.rows.forEach(row => {
        row._showDetails = this.allDetailsShowing;
      });
    },
    deselectAll() {
      this.allSelected = false;
      this.tableData.rows.forEach(c => (c.isSelected = false));
      this.tableData.selectedRow = null;
    },
    refresh() {
      this.$root.$emit('bv::refresh::table', 'data-table');
    },
    resetSelectedRow() {
      this.tableData.selectedRow = null;
    },
    onEdit(row) {
      this.$emit('edit', row);
    }
  }
};
</script>

<style scoped lang="scss">
/deep/ .b-table-sticky-header {
  max-height: 100%;
}

// @media (max-width: 991px) {
//   .main-content {
//     max-height: calc(100vh - 100px);
//   }
// }

.row-details-btn {
  display: inline-block;
  position: relative;
  cursor: pointer;
  margin-left: 0;

  &-spaced {
    margin-left: 5px;
  }
}

/deep/ .filter-container {
  margin-top: 5px;
}

/deep/ table {
  th.check,
  td.check {
    width: 5%;
  }

  th.actions,
  td.actions {
    text-align: center;
  }
}
</style>
